import { cn } from '@seen/ui'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { VirtualizedList } from '#src/components/core/VirtualizedList.tsx'
import { DocumentPickerEmptyState } from '#src/components/document-picker/DocumentPickerEmptyState.tsx'
import { DocumentPickerItem } from '#src/components/document-picker/DocumentPickerItem.tsx'
import { DocumentPickerToolbar } from '#src/components/document-picker/DocumentPickerToolbar.tsx'
import type { DocumentPickerDocument } from '#src/components/document-picker/DocumentPickerView.tsx'
import type { Filter, FilterConfigMap } from '#src/components/filter/Filter.tsx'
import { useFilteredData } from '#src/components/filter/useFilteredData.ts'
import { useClientSideSearch } from '#src/hooks/useClientSideSearch.ts'
import { extractTextFromHTML } from '#src/utils/html.ts'

interface DocumentPickerListProps {
  documents: DocumentPickerDocument[]
  selectedIds: Set<string>
  activeDocumentId?: string | null
  onSelectionChange?: (ids: string[]) => void
  onActiveDocumentChange?: (id: string) => void
  onItemDoubleClick?: (id: string) => void
  onLoadMore?: () => void
  hasMore?: boolean
  isLoadingMore?: boolean
  filters: Filter[]
  filterOptions: FilterConfigMap<DocumentPickerDocument>
  className?: string
}

export function DocumentPickerList({
  documents,
  selectedIds,
  activeDocumentId,
  onSelectionChange,
  onActiveDocumentChange,
  onItemDoubleClick,
  onLoadMore,
  hasMore,
  isLoadingMore,
  filters,
  filterOptions,
  className,
}: DocumentPickerListProps) {
  const [search, setSearch] = useState('')
  const [scrollToIndex, setScrollToIndex] = useState<number | undefined>(
    undefined
  )
  const lastClickedIndexRef = useRef<number | null>(null)

  /* Reset range-selection anchor when filtered view changes */
  useEffect(() => {
    lastClickedIndexRef.current = null
  }, [search, filters])

  const filteredDocuments = useFilteredData(documents, filterOptions, filters)

  /* BM25 full-text search with stemming, fuzzy tolerance, and diacritic
   * folding — replaces the previous substring `.includes()` filter.
   * Field boosts match DocumentReferencesTable: title highest, then
   * aiSummary, author, description, and transcription. idleBuild defers
   * the index construction off the main thread; the list stays responsive
   * while indexing. */
  const {
    search: searchDocs,
    isIndexing,
    getExtractedFields,
  } = useClientSideSearch(filteredDocuments, {
    fields: ['title', 'description', 'aiSummary', 'author', 'transcription'],
    boost: {
      title: 5,
      aiSummary: 3,
      author: 2,
      description: 1.5,
      transcription: 1,
    },
    /* subtitle is intentionally not indexed: it is always
     * `description || aiSummary` (set at both call sites), so its tokens are
     * already covered by the stripped description/aiSummary fields. Indexing
     * it would re-index raw description HTML (double-scoring its source and
     * matching tag names like "span"/"strong" via prefix search). */
    extractFields: (doc) => ({
      title: doc.title,
      /* description may be HTML (RichTextEditor output) — strip tags so
       * MiniSearch tokenises "diabetes" not "<p>diabetes". aiSummary is
       * plaintext today but stripped defensively. */
      description: extractTextFromHTML(doc.description ?? ''),
      aiSummary: extractTextFromHTML(doc.aiSummary ?? ''),
      author: doc.author ?? '',
      transcription: doc.transcription ?? '',
    }),
    idleBuild: true,
  })

  const { items: visibleDocuments, matchById } = useMemo(
    () => searchDocs(search),
    [searchDocs, search]
  )

  const selectedVisibleCount = visibleDocuments.filter((doc) =>
    selectedIds.has(doc.id)
  ).length
  const allVisibleSelected =
    visibleDocuments.length > 0 &&
    selectedVisibleCount === visibleDocuments.length
  const someVisibleSelected = selectedVisibleCount > 0

  const selectAllChecked: boolean | 'indeterminate' = allVisibleSelected
    ? true
    : someVisibleSelected
      ? 'indeterminate'
      : false

  const handleSelectAll = useCallback(() => {
    if (!onSelectionChange) {
      return
    }

    if (allVisibleSelected) {
      onSelectionChange(
        [...selectedIds].filter(
          (selectedId) => !visibleDocuments.some((doc) => doc.id === selectedId)
        )
      )
    } else {
      const merged = new Set(selectedIds)
      for (const doc of visibleDocuments) {
        merged.add(doc.id)
      }
      onSelectionChange([...merged])
    }
  }, [onSelectionChange, allVisibleSelected, selectedIds, visibleDocuments])

  const activeIndex = visibleDocuments.findIndex(
    (d) => d.id === activeDocumentId
  )

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (visibleDocuments.length === 0 || !onActiveDocumentChange) {
        return
      }

      let nextIndex: number | null = null

      if (e.key === 'ArrowDown' || e.key === 'j') {
        e.preventDefault()
        if (activeIndex < 0) {
          nextIndex = 0
        } else if (activeIndex < visibleDocuments.length - 1) {
          nextIndex = activeIndex + 1
        }
      } else if (e.key === 'ArrowUp' || e.key === 'k') {
        e.preventDefault()
        if (activeIndex > 0) {
          nextIndex = activeIndex - 1
        }
      }

      if (e.key === 'x' || e.key === 'X') {
        e.preventDefault()
        if (activeDocumentId && onSelectionChange) {
          if (
            e.shiftKey &&
            lastClickedIndexRef.current !== null &&
            activeIndex >= 0
          ) {
            const from = Math.min(lastClickedIndexRef.current, activeIndex)
            const to = Math.max(lastClickedIndexRef.current, activeIndex)
            const rangeIds = visibleDocuments
              .slice(from, to + 1)
              .map((d) => d.id)
            const merged = new Set([...selectedIds, ...rangeIds])
            onSelectionChange([...merged])
          } else {
            const isCurrentlySelected = selectedIds.has(activeDocumentId)
            if (isCurrentlySelected) {
              onSelectionChange(
                [...selectedIds].filter((id) => id !== activeDocumentId)
              )
            } else {
              onSelectionChange([...selectedIds, activeDocumentId])
            }
          }
          if (activeIndex >= 0) {
            lastClickedIndexRef.current = activeIndex
          }
        }
        return
      }

      if (nextIndex !== null) {
        onActiveDocumentChange(visibleDocuments[nextIndex].id)
        setScrollToIndex(nextIndex)
      }
    },
    [
      visibleDocuments,
      activeIndex,
      activeDocumentId,
      selectedIds,
      onActiveDocumentChange,
      onSelectionChange,
    ]
  )

  const isSearchActive = search.trim().length > 0

  const rows = useMemo(
    () =>
      visibleDocuments.map((doc, index) => (
        <DocumentPickerItem
          key={doc.id}
          document={doc}
          isSelected={selectedIds.has(doc.id)}
          isActive={activeDocumentId === doc.id}
          matchTerms={
            isSearchActive ? (matchById.get(doc.id)?.terms ?? []) : undefined
          }
          extractedFields={
            isSearchActive ? getExtractedFields(doc.id) : undefined
          }
          onSelectionChange={(selected, shiftKey) => {
            if (!onSelectionChange) {
              return
            }
            if (shiftKey && lastClickedIndexRef.current !== null) {
              const from = Math.min(lastClickedIndexRef.current, index)
              const to = Math.max(lastClickedIndexRef.current, index)
              const rangeIds = visibleDocuments
                .slice(from, to + 1)
                .map((d) => d.id)
              const merged = new Set([...selectedIds, ...rangeIds])
              onSelectionChange([...merged])
            } else {
              const next = selected
                ? [...selectedIds, doc.id]
                : [...selectedIds].filter((id) => id !== doc.id)
              onSelectionChange(next)
            }
            lastClickedIndexRef.current = index
          }}
          onActiveChange={() => onActiveDocumentChange?.(doc.id)}
          onDoubleClick={() => onItemDoubleClick?.(doc.id)}
        />
      )),
    [
      visibleDocuments,
      selectedIds,
      activeDocumentId,
      isSearchActive,
      matchById,
      getExtractedFields,
      onSelectionChange,
      onActiveDocumentChange,
      onItemDoubleClick,
    ]
  )

  return (
    // oxlint-disable-next-line jsx-a11y/no-noninteractive-element-interactions -- the list owns arrow/j/k/x keyboard navigation for its rows
    <section
      role="application"
      // oxlint-disable-next-line jsx-a11y/no-noninteractive-tabindex -- keyboard nav for list
      tabIndex={0}
      className={cn('flex h-full flex-col outline-hidden', className)}
      onKeyDown={handleKeyDown}
    >
      <DocumentPickerToolbar
        className="shrink-0 border-b"
        search={search}
        onSearchChange={setSearch}
        selectAllChecked={selectAllChecked}
        onSelectAllChange={handleSelectAll}
        selectAllDisabled={visibleDocuments.length === 0}
      />
      {isSearchActive && isIndexing ? (
        <p className="flex flex-1 items-center justify-center text-sm text-muted-foreground">
          Preparing search…
        </p>
      ) : visibleDocuments.length === 0 ? (
        <DocumentPickerEmptyState className="flex-1" />
      ) : (
        <VirtualizedList
          rows={rows}
          estimateSize={() => 56}
          dynamic={true}
          scrollRestorationId={null}
          scrollToIndex={scrollToIndex}
          onLoadMore={onLoadMore}
          hasMore={hasMore}
          isLoadingMore={isLoadingMore}
        />
      )}
    </section>
  )
}
